(() => {
  if (window.ScrptdProductAddToCart) return;

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const normalize = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const getVariantDataNode = (form) => form.querySelector('[data-type="product-variations-data"]');

  const getVariants = (form) => {
    const node = getVariantDataNode(form);
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('SCRPTD: Variant data could not be read.', error);
      return [];
    }
  };

  const saveVariants = (form, variants) => {
    const node = getVariantDataNode(form);
    if (node) node.textContent = JSON.stringify(variants);
  };

  const getSelectedPillValue = (fieldset) => {
    const pill = fieldset?.querySelector(
      '[data-node-type="add-to-cart-option-pill"].is-selected, ' +
      '[data-node-type="add-to-cart-option-pill"].w--ecommerce-pill-selected'
    );
    return pill?.dataset.scrptdSizeValue || pill?.dataset.optionName || '';
  };

  const alignSelectsWithVisibleSelection = (form) => {
    form.querySelectorAll('[data-node-type="add-to-cart-option"]').forEach((fieldset) => {
      const select = fieldset.querySelector('[data-node-type="add-to-cart-option-select"]');
      const selectedPillValue = getSelectedPillValue(fieldset);
      if (!select || !selectedPillValue) return;

      const matchingOption = Array.from(select.options).find(
        (option) => normalize(option.value) === normalize(selectedPillValue)
      );
      if (matchingOption) select.value = matchingOption.value;
    });
  };

  const selectedOptionValues = (form) => {
    alignSelectsWithVisibleSelection(form);
    return Array.from(
      form.querySelectorAll('[data-node-type="add-to-cart-option-select"]')
    ).map((select) => normalize(select.value));
  };

  const getSelectedVariant = (form) => {
    const variants = getVariants(form);
    const selectedOptions = selectedOptionValues(form);

    if (!selectedOptions.length) {
      const id = form.querySelector('input[name="id"]')?.value;
      return variants.find((variant) => String(variant.id) === String(id)) || null;
    }

    return variants.find((variant) => selectedOptions.every((value, index) => (
      normalize(variant[`option${index + 1}`]) === value
    ))) || null;
  };

  const getErrorNodes = (form) => {
    const wrapper = form.closest('.add-to-cart')?.querySelector(
      '[data-scrptd-product-error], [data-node-type="commerce-add-to-cart-error"]'
    );
    return {
      wrapper,
      text: wrapper?.querySelector(
        '[data-scrptd-product-error-text], [data-node-type="commerce-add-to-cart-error"]'
      )
    };
  };

  const showError = (form, message) => {
    const { wrapper, text } = getErrorNodes(form);
    if (text) text.textContent = message;
    if (wrapper) wrapper.style.display = 'block';
  };

  const clearError = (form) => {
    const { wrapper } = getErrorNodes(form);
    if (wrapper) wrapper.style.display = 'none';
  };

  const syncVariant = (form) => {
    const variant = getSelectedVariant(form);
    const idInput = form.querySelector('input[name="id"]');
    const submit = form.querySelector(
      '[data-scrptd-product-submit], [data-node-type="commerce-add-to-cart-button"]'
    );
    const labels = form.closest('[data-scrptd-add-label]')?.dataset || {};

    if (variant && idInput) idInput.value = variant.id;
    if (submit && variant) {
      submit.disabled = !variant.available;
      submit.value = variant.available
        ? (labels.scrptdAddLabel || submit.value)
        : (labels.scrptdSoldLabel || 'AUSVERKAUFT');
    }
    return variant;
  };

  const updateLiveAvailability = (form, liveVariant) => {
    const variants = getVariants(form);
    const embeddedVariant = variants.find(
      (variant) => String(variant.id) === String(liveVariant.id)
    );
    if (embeddedVariant) {
      embeddedVariant.available = Boolean(liveVariant.available);
      saveVariants(form, variants);
    }

    if (!liveVariant.available) {
      form.querySelectorAll(
        '[data-node-type="add-to-cart-option-pill"].is-selected, ' +
        '[data-node-type="add-to-cart-option-pill"].w--ecommerce-pill-selected'
      ).forEach((pill) => {
        pill.disabled = true;
        pill.setAttribute('aria-disabled', 'true');
        pill.classList.add('is-unavailable', 'w--ecommerce-pill-disabled');
      });
    }
  };

  const refreshLiveVariant = async (form) => {
    const handle = form.closest('[data-scrptd-product-handle]')?.dataset.scrptdProductHandle;
    if (!handle) return null;

    const response = await fetch(`${rootPath()}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) return null;

    const product = await response.json();
    const values = selectedOptionValues(form);
    const liveVariant = product.variants?.find((variant) => (
      (variant.options || []).every((value, index) => normalize(value) === values[index])
    )) || null;

    if (liveVariant) updateLiveAvailability(form, liveVariant);
    return liveVariant;
  };

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-node-type="add-to-cart-option-select"]');
    const form = select?.closest('form[action*="/cart/add"]');
    if (form) window.setTimeout(() => syncVariant(form), 0);
  }, true);

  document.addEventListener('click', (event) => {
    const submit = event.target.closest('[data-scrptd-product-submit]');
    if (!submit) return;
    const form = submit.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || !form.closest('[data-scrptd-product-handle]')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    clearError(form);

    const variant = syncVariant(form);
    const submit = form.querySelector(
      '[data-scrptd-product-submit], [data-node-type="commerce-add-to-cart-button"]'
    );
    if (!variant || !variant.available) {
      showError(
        form,
        variant
          ? 'Diese Groesse ist aktuell nicht verfuegbar.'
          : 'Bitte waehle eine verfuegbare Groesse aus.'
      );
      return;
    }

    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
    }

    try {
      const payload = new FormData(form);
      payload.set('id', variant.id);
      ['option1', 'option2', 'option3'].forEach((name) => payload.delete(name));

      const response = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: payload
      });
      const result = await response.json();
      if (!response.ok) {
        const requestError = new Error(
          result.description || 'Der Artikel konnte nicht hinzugefuegt werden.'
        );
        requestError.status = response.status;
        throw requestError;
      }

      clearError(form);
      const expectedLine = {
        id: Number(variant.id),
        quantity: Math.max(1, Number(result.quantity) || 1)
      };
      const cart = typeof window.ScrptdFetchConfirmedCart === 'function'
        ? await window.ScrptdFetchConfirmedCart([expectedLine])
        : await fetch(`${rootPath()}cart.js?_=${Date.now()}`, {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }
          }).then((cartResponse) => cartResponse.json());

      if (typeof window.openCartDrawerWithCart === 'function') {
        window.setTimeout(() => window.openCartDrawerWithCart(cart, submit), 0);
      } else if (typeof window.openCartDrawer === 'function') {
        const openDrawer = window.openCartDrawer;
        window.setTimeout(() => {
          Promise.resolve(openDrawer(submit)).catch((drawerError) => {
            console.error('SCRPTD: Der Warenkorb konnte nicht geoeffnet werden.', drawerError);
          });
        }, 0);
      } else {
        document.dispatchEvent(new CustomEvent('scrptd:cart-change', { detail: cart }));
      }
    } catch (error) {
      if (error.status === 422) {
        try {
          const liveVariant = await refreshLiveVariant(form);
          syncVariant(form);
          showError(
            form,
            liveVariant && !liveVariant.available
              ? 'Diese Groesse ist inzwischen ausverkauft. Bitte waehle eine andere Groesse.'
              : (error.message || 'Diese Variante ist aktuell nicht verfuegbar.')
          );
        } catch (_) {
          showError(form, error.message || 'Diese Variante ist aktuell nicht verfuegbar.');
        }
      } else {
        showError(form, error.message || 'Der Artikel konnte nicht hinzugefuegt werden.');
      }
    } finally {
      if (submit) {
        submit.removeAttribute('aria-busy');
        syncVariant(form);
      }
    }
  }, true);

  const initialize = (scope = document) => {
    scope.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.closest('[data-scrptd-product-handle]')) syncVariant(form);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  window.ScrptdProductAddToCart = { syncVariant };
})();
